'use client';

import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { useEffect, useCallback, useState, useRef } from 'react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Code,
  Link as LinkIcon,
  Undo,
  Redo,
  Minus,
  Palette,
  Highlighter
} from 'lucide-react';
import { cn } from '#/lib/utils';

// ─── 툴바 버튼 ──────────────────────────────────────────

function ToolbarButton({
  onClick,
  active = false,
  disabled = false,
  children,
  title
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'p-1.5 rounded-md transition-colors',
        active
          ? 'bg-[#104573]/15 text-foreground'
          : 'text-muted-foreground hover:bg-[#104573]/[0.05] hover:text-foreground',
        disabled && 'opacity-40 cursor-not-allowed'
      )}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-[#104573]/15 mx-1" />;
}

// ─── 색상 팔레트 ─────────────────────────────────────────

const COLOR_PRESETS = [
  { label: '기본', value: '' },
  { label: '검정', value: '#000000' },
  { label: '진회색', value: '#4B5563' },
  { label: '빨강', value: '#DC2626' },
  { label: '주황', value: '#EA580C' },
  { label: '노랑', value: '#CA8A04' },
  { label: '초록', value: '#16A34A' },
  { label: '청록', value: '#0D9488' },
  { label: '파랑', value: '#2563EB' },
  { label: '남색', value: '#4F46E5' },
  { label: '보라', value: '#9333EA' },
  { label: '분홍', value: '#DB2777' }
];

function ColorPicker({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentColor = editor.getAttributes('textStyle').color || '';

  const applyColor = (color: string) => {
    if (color === '') {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().setColor(color).run();
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="텍스트 색상"
        className={cn(
          'p-1.5 rounded-md transition-colors flex items-center gap-0.5',
          currentColor
            ? 'bg-[#104573]/15 text-foreground'
            : 'text-muted-foreground hover:bg-[#104573]/[0.05] hover:text-foreground'
        )}
      >
        <Palette className="w-4 h-4" />
        <div
          className="w-3 h-1.5 rounded-sm border border-[#104573]/20"
          style={{ backgroundColor: currentColor || '#000000' }}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-popover rounded-lg shadow-lg border border-[#104573]/10 p-2 w-[168px]">
          <div className="grid grid-cols-6 gap-1">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c.value || 'default'}
                type="button"
                title={c.label}
                onClick={() => applyColor(c.value)}
                className={cn(
                  'w-6 h-6 rounded-md border transition-all hover:scale-110',
                  currentColor === c.value
                    ? 'ring-2 ring-blue-500 ring-offset-1'
                    : 'border-[#104573]/10 hover:border-[#104573]/40'
                )}
                style={{
                  backgroundColor: c.value || '#ffffff',
                  ...(c.value === '' && {
                    backgroundImage:
                      'linear-gradient(135deg, #fff 45%, #dc2626 45%, #dc2626 55%, #fff 55%)'
                  })
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 배경색 팔레트 ───────────────────────────────────────

const BG_COLOR_PRESETS = [
  { label: '없음', value: '' },
  { label: '연노랑', value: '#FEF9C3' },
  { label: '연주황', value: '#FFEDD5' },
  { label: '연분홍', value: '#FCE7F3' },
  { label: '연보라', value: '#F3E8FF' },
  { label: '연파랑', value: '#DBEAFE' },
  { label: '연청록', value: '#CCFBF1' },
  { label: '연초록', value: '#DCFCE7' },
  { label: '연회색', value: '#F3F4F6' },
  { label: '노랑', value: '#FDE047' },
  { label: '주황', value: '#FDBA74' },
  { label: '빨강', value: '#FCA5A5' }
];

function BgColorPicker({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentColor = editor.getAttributes('highlight').color || '';

  const applyColor = (color: string) => {
    if (color === '') {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setHighlight({ color }).run();
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="배경색"
        className={cn(
          'p-1.5 rounded-md transition-colors flex items-center gap-0.5',
          currentColor
            ? 'bg-[#104573]/15 text-foreground'
            : 'text-muted-foreground hover:bg-[#104573]/[0.05] hover:text-foreground'
        )}
      >
        <Highlighter className="w-4 h-4" />
        <div
          className="w-3 h-1.5 rounded-sm border border-[#104573]/20"
          style={{ backgroundColor: currentColor || '#FDE047' }}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-popover rounded-lg shadow-lg border border-[#104573]/10 p-2 w-[168px]">
          <div className="grid grid-cols-6 gap-1">
            {BG_COLOR_PRESETS.map((c) => (
              <button
                key={c.value || 'none'}
                type="button"
                title={c.label}
                onClick={() => applyColor(c.value)}
                className={cn(
                  'w-6 h-6 rounded-md border transition-all hover:scale-110',
                  currentColor === c.value
                    ? 'ring-2 ring-blue-500 ring-offset-1'
                    : 'border-[#104573]/10 hover:border-[#104573]/40'
                )}
                style={{
                  backgroundColor: c.value || '#ffffff',
                  ...(c.value === '' && {
                    backgroundImage:
                      'linear-gradient(135deg, #fff 45%, #dc2626 45%, #dc2626 55%, #fff 55%)'
                  })
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 툴바 ────────────────────────────────────────────────

function EditorToolbar({ editor }: { editor: Editor }) {
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL을 입력하세요:', previousUrl);

    if (url === null) return; // 취소
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[#104573]/10 p-2 bg-[#104573]/[0.03] rounded-t-lg">
      {/* 텍스트 스타일 */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="굵게 (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="기울임 (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="밑줄 (Ctrl+U)"
      >
        <UnderlineIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
        title="취소선"
      >
        <Strikethrough className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* 헤딩 */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        title="제목 2"
      >
        <Heading2 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        title="제목 3"
      >
        <Heading3 className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* 리스트 */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="글머리 기호 목록"
      >
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="번호 매기기 목록"
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* 블록 */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')}
        title="인용문"
      >
        <Quote className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        active={editor.isActive('codeBlock')}
        title="코드 블록"
      >
        <Code className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="구분선"
      >
        <Minus className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* 링크 */}
      <ToolbarButton
        onClick={setLink}
        active={editor.isActive('link')}
        title="링크"
      >
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* 글자색 */}
      <ColorPicker editor={editor} />
      {/* 배경색 */}
      <BgColorPicker editor={editor} />

      <ToolbarDivider />

      {/* 실행 취소/다시 실행 */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="실행 취소 (Ctrl+Z)"
      >
        <Undo className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="다시 실행 (Ctrl+Shift+Z)"
      >
        <Redo className="w-4 h-4" />
      </ToolbarButton>
    </div>
  );
}

// ─── 에디터 본체 ─────────────────────────────────────────

interface RichTextEditorProps {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
  minHeight?: string;
}

export default function RichTextEditor({
  content = '',
  onChange,
  placeholder = '내용을 입력하세요...',
  className,
  editable = true,
  minHeight = '200px'
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] }
      }),
      Placeholder.configure({ placeholder }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#104573] dark:text-[#60a3d9] underline hover:text-[#082640] dark:hover:text-[#8dbfe8]'
        }
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true })
    ],
    content,
    editable,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm sm:prose-base dark:prose-invert max-w-none focus:outline-none px-4 py-3',
          'prose-headings:text-foreground prose-headings:font-semibold',
          'prose-p:text-foreground/80 prose-p:leading-relaxed',
          'prose-blockquote:border-l-4 prose-blockquote:border-[#104573]/20 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground',
          '[&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none',
          'prose-code:bg-[#104573]/[0.05] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:text-foreground',
          'prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg',
          '[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-4',
          '[&_pre_code]:bg-transparent [&_pre_code]:text-gray-100 [&_pre_code]:p-0',
          'prose-a:text-[#104573] dark:prose-a:text-[#60a3d9] prose-a:underline',
          'prose-ul:list-disc prose-ol:list-decimal',
          'prose-li:text-foreground/80'
        ),
        style: `min-height: ${minHeight}`
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // 빈 에디터의 경우 빈 문자열로 반환
      const isEmpty = editor.isEmpty;
      onChange?.(isEmpty ? '' : html);
    },
    // SSR에서는 에디터를 렌더링하지 않음
    immediatelyRender: false
  });

  // 외부에서 content가 변경되면 에디터 내용 업데이트
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return (
      <div
        className={cn(
          'rounded-lg border border-[#104573]/10 bg-card animate-pulse',
          className
        )}
        style={{ minHeight }}
      />
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-[#104573]/10 bg-card overflow-hidden',
        'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
        className
      )}
    >
      {editable && <EditorToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

// ─── 읽기 전용 HTML 뷰어 ─────────────────────────────────

export function RichTextViewer({
  content,
  className
}: {
  content: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'prose prose-sm sm:prose-base dark:prose-invert max-w-none',
        'prose-headings:text-foreground prose-headings:font-semibold',
        'prose-p:text-foreground/80 prose-p:leading-relaxed',
        'prose-blockquote:border-l-4 prose-blockquote:border-[#104573]/20 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground',
        '[&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none',
        'prose-code:bg-[#104573]/[0.05] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm',
        'prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg',
        'prose-a:text-[#104573] dark:prose-a:text-[#60a3d9] prose-a:underline',
        'prose-ul:list-disc prose-ol:list-decimal',
        'prose-li:text-foreground/80',
        className
      )}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}
